import { z } from 'zod';

import LoggingService from 'components/core/logging/LoggingService';

/**
 * Builds a Zod schema for the JSON string that the backend stashes in a
 * `SelectOption.data` field. Although the GraphQL schema types `data` as an
 * object, the value arrives as a serialized JSON string, so this parses the
 * string first and then validates the result against `shape` — generalizing the
 * media-gallery placement precedent to every "SelectOption.data is really a JSON
 * string" boundary.
 *
 * Pass the raw `data` value straight to `.safeParse()`. An absent/non-string
 * value fails at `.string()` (the normal "no metadata" case, which is not
 * logged), a shape mismatch fails silently at `.pipe(shape)`, and only genuinely
 * malformed JSON is surfaced to Datadog before the parse fails. Read the result
 * via `.safeParse(data).data` — the validated shape, or `undefined` on any
 * failure, so callers degrade gracefully.
 *
 * @param shape - Zod schema describing the expected parsed object.
 * @param loggingInfo - Context attached to the Datadog log when `JSON.parse` throws.
 * @returns A schema whose `.safeParse(data).data` is the validated shape or `undefined`.
 */
export const selectOptionDataSchema = <Shape extends z.ZodType>(
  shape: Shape,
  loggingInfo = 'Error parsing SelectOption metadata'
) =>
  z
    .string()
    .transform((value, context) => {
      if (value.trim() === '') {
        context.addIssue({ code: 'custom', message: 'Empty metadata' });

        return z.NEVER;
      }

      try {
        return JSON.parse(value);
      } catch (error) {
        if (error instanceof Error) {
          LoggingService.captureException(error, { info: loggingInfo });
        }

        context.addIssue({ code: 'custom', message: 'Invalid JSON' });

        return z.NEVER;
      }
    })
    .pipe(shape);
